// UploadModal.tsx
import React, { useState, useRef } from 'react';
import { Upload, X } from 'lucide-react';
import { assetsApi } from '../services/assetsApi';

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadComplete: () => void;
}

const UploadModal: React.FC<UploadModalProps> = ({ isOpen, onClose, onUploadComplete }) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [isSlowMotion, setIsSlowMotion] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isVideo = file?.type.startsWith('video/') ?? false;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError('');
      setIsSlowMotion(false);
    }
  };

  const resetForm = () => {
    setFile(null);
    setError('');
    setIsSlowMotion(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    try {
      setUploading(true);
      setError('');

      const formData = new FormData();
      formData.append('file', file);
      // Optimise videos to 1080p/30fps unless the uploader marked it as slow-motion.
      formData.append('optimise', String(isVideo && !isSlowMotion));

      await assetsApi.uploadAsset(formData);
      resetForm();
      onUploadComplete();
      onClose();
    } catch (err) {
      setError('Failed to upload file: ' + (err instanceof Error ? err.message : String(err)));
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Upload Asset</h2>
          <button 
            onClick={() => {
              resetForm();
              onClose();
            }} 
            className="text-gray-500 hover:text-gray-700"
            type="button"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="mb-4">
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileChange}
              className="hidden"
              id="file-upload"
              accept="image/*,video/*"
            />
            <label
              htmlFor="file-upload"
              className="cursor-pointer flex flex-col items-center"
            >
              <Upload className="w-12 h-12 text-gray-400 mb-2" />
              <span className="text-gray-600">
                {file ? file.name : 'Click to select file'}
              </span>
              <span className="text-sm text-gray-500 mt-2">
                Supported formats: Images and Videos
              </span>
            </label>
          </div>
        </div>

        {isVideo && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-100 rounded text-sm">
            <p className="text-blue-800">
              Videos are optimised to <strong>1080p</strong> at <strong>30fps</strong> (if larger) so they
              play smoothly on your screens. This won't change how it looks unless it's slow-motion footage.
            </p>
            <label className="flex items-center mt-2 text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={isSlowMotion}
                onChange={(e) => setIsSlowMotion(e.target.checked)}
                className="mr-2"
              />
              This is slow-motion footage — upload at full quality (don't optimise)
            </label>
          </div>
        )}

        {error && (
          <div className="mb-4 p-2 text-red-600 bg-red-50 rounded">
            {error}
          </div>
        )}

        <div className="flex justify-end space-x-3">
          <button
            onClick={() => {
              resetForm();
              onClose();
            }}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
            type="button"
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={uploading || !file}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            type="button"
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadModal;